import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  renderTestOrderAssayChip,
  renderTestOrderContainerChip,
  renderTestOrderSample,
  renderTestOrderTestDefinitionChip,
} from 'sample-tests/ui/utils/testOrderChipsUtil';
import {TestOrderStatusDisplay} from 'sample-tests/ui/utils/TestOrderStatusUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_DARK} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
} from 'ui-library/scrollable/ScrollableTableConstants';

import {TestOrderListingGraphQL} from '../../../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';

interface TestOrderDisplayRowData {
  testOrder: JSX.Element;
  status: JSX.Element | string;
  dilutionFactor: number | null;
  container: JSX.Element | string | null;
  sample: JSX.Element;
  testDefinition: JSX.Element;
  assay: JSX.Element | null;
}

interface TestOrderDisplayRow extends ScrollableTableRow {
  data: TestOrderDisplayRowData;
}

interface TestOrderDisplayTableProps {
  testOrders: Array<TestOrderListingGraphQL>;
  excludeColumns?: Array<keyof TestOrderDisplayRowData>;
  tableKey: string;
  /** If provided, selection will be enabled & controlled by the provided props. Otherwise no selection. */
  selectionProps?: {
    selectedTestOrderIds: Set<string>;
    rowSelectionMode: RowSelectionMode;
    onRowsSelect: (rows: Array<TestOrderDisplayRow>, isSelected: boolean) => void;
    isSelected: (testOrderId: string) => boolean;
  };
}

export default function TestOrderDisplayTable(props: TestOrderDisplayTableProps): JSX.Element {
  const allColumnHeaders: Array<ScrollableTableColumn & {accessorKey: keyof TestOrderDisplayRowData}> = [
    {header: 'Test order', accessorKey: 'testOrder'},
    {header: 'Status', accessorKey: 'status', width: 140},
    {header: 'Dilution factor', accessorKey: 'dilutionFactor', width: 125},
    {header: 'Test definition', accessorKey: 'testDefinition'},
    {header: 'Container', accessorKey: 'container'},
    {header: 'Sample', accessorKey: 'sample'},
    {header: 'Assay', accessorKey: 'assay'},
  ];
  const shownColumnHeaders =
    props.excludeColumns != null
      ? allColumnHeaders.filter((header) => !props.excludeColumns!.includes(header.accessorKey))
      : allColumnHeaders;
  const formattedColumnHeaders = [
    ...shownColumnHeaders.slice(0, -1),
    {...shownColumnHeaders.slice(-1)[0], flexGrow: 1},
  ];

  const rows: Array<TestOrderDisplayRow> = props.testOrders.map((testOrder) => {
    const rowId = testOrder.id;
    return {
      id: rowId,
      data: {
        // TODO(BNCH-110671): Use a test order chip instead
        testOrder: (
          <Tooltip text={testOrder.name}>
            <span>{testOrder.name}</span>
          </Tooltip>
        ),
        status: <TestOrderStatusDisplay status={testOrder.status} />,
        dilutionFactor: testOrder.dilutionFactor,
        container:
          testOrder.container == null ? (
            <span className={css(styles.noneText)}>None</span>
          ) : (
            renderTestOrderContainerChip(testOrder, ChipSize.TABLE_CELL)
          ),
        sample: renderTestOrderSample(testOrder),
        testDefinition: renderTestOrderTestDefinitionChip(testOrder, ChipSize.TABLE_CELL),
        assay: renderTestOrderAssayChip(testOrder, ChipSize.TABLE_CELL),
      },
      isSelected: props.selectionProps?.isSelected(rowId),
    };
  });

  return (
    <ScrollableTable
      rowSelectionMode={props.selectionProps?.rowSelectionMode}
      disableSelection={props.selectionProps == null}
      headerClassName={css(styles.modalColumnHeader)}
      columns={formattedColumnHeaders}
      rows={rows}
      tableKey={props.tableKey}
      canResizeColumns={true}
      onRowsSelect={props.selectionProps?.onRowsSelect}
    />
  );
}

const styles = StyleSheet.create({
  modalColumnHeader: {
    fontSize: '13px',
    fontWeight: 600,
  },
  noneText: {
    color: GRAY_DARK,
  },
});
